import React, { useEffect, useRef, useState } from 'react'
import Form from 'react-bootstrap/Form'
import Table from 'react-bootstrap/Table'
import Button from 'react-bootstrap/Button'
import { format } from 'date-fns'
import { connect, useDispatch } from 'react-redux'
import * as R from 'ramda'
import { isAdmin as checkIsAdmin } from '../admin'
import { nameById as nameCountryById } from '../../js/countries'
import { nameById as nameStateById } from '../../js/states'
import { loadCourseGroups } from '../../store/actions'
import MergePanel from './MergePanel'
import { hiddenReasons } from './visibility'

// Swedish collation so å/ä/ö sort at the end of the alphabet rather than as a/o.
const swedishCollator = new Intl.Collator('sv')
const swedishCompare = (a, b) => swedishCollator.compare(a || '', b || '')

const SORT_CONFIG = {
  initialSort: [
    { key: 'updated', order: 'desc' },
    { key: 'name', order: 'desc' },
  ],
  columns: {
    id: {
      filter: '',
      sortOptions: ['desc', 'asc', undefined],
      sortFn: (a, b) => a - b,
    },
    name: {
      filter: '',
      sortOptions: ['desc', 'asc', undefined],
      sortFn: swedishCompare,
    },
    holes: {
      filter: '',
      sortOptions: ['desc', 'asc', undefined],
      sortFn: swedishCompare,
    },
    count: {
      filter: '',
      sortOptions: ['desc', 'asc', undefined],
      sortFn: (a, b) => a - b,
    },
    location: {
      filter: '',
      sortOptions: ['asc', 'desc', undefined],
      sortFn: (a, b) => (!!a ? 1 : 0) - (!!b ? 1 : 0),
    },
    updated: {
      filter: '',
      sortOptions: ['asc', 'desc', undefined],
      sortFn: (a, b) => a - b,
    },
    played: {
      filter: '',
      sortOptions: ['desc', 'asc', undefined],
      sortFn: (a, b) => (a || 0) - (b || 0),
    },
  },
}

const adminRenameGroup = (id, name, secondaryName) => ({
  type: 'ADMIN_RENAME_GROUP',
  payload: {
    request: {
      url: `/v3/course/rename`,
      method: 'post',
      data: { id, name, secondaryName },
    },
  },
})

// Hide, or flag. `admin/course/{id}/setState` takes the three flags independently and reads
// null as "leave alone", so each button sends only the one it is about -- and each has an
// inverse, which is why these are toggles rather than the curation page's one-way batch.
const adminSetCourseState = (id, changes) => ({
  type: 'ADMIN_SET_COURSE_STATE',
  payload: {
    request: {
      url: `/admin/course/${id}/setState`,
      method: 'post',
      data: changes,
    },
  },
})

function GroupsTable({
  isAdmin = false,
  groups = [],
  initialSelected = [],
  played = null,
  visibility = null,
  onCourseStateChanged = () => {},
  onCourseGroupClicked = () => {},
}) {
  const dispatch = useDispatch()

  const [sort, setSort] = useState(SORT_CONFIG.initialSort)
  const [admin, setAdmin] = useState({
    selected: [],
    name: '',
    secondaryName: '',
  })
  const seededRef = useRef(false)
  // What one selected course currently is: hidden or not. The public course
  // listing carries no such flag, so it is read from the merge preview, which reports it
  // for every course it describes.
  const [courseState, setCourseState] = useState(null)
  const [busy, setBusy] = useState(false)
  const arrivedRef = useRef(null)
  const scrolledRef = useRef(false)

  // Seeded in an effect rather than in the initial state, because the course list is
  // fetched once at app boot and is very often still empty on this component's first
  // render — a selection built then would be built against no rows at all, and the
  // rename fields would prefill from a course that is not there yet.
  useEffect(() => {
    if (seededRef.current) return
    const seed = isAdmin ? initialSelected : []
    if (seed.length === 0) return

    seededRef.current = true
    const only = seed.length === 1 && groups.find((group) => group.id === seed[0])
    setAdmin({
      selected: seed,
      name: only ? only.name : '',
      secondaryName: only ? only.secondaryName : '',
    })
  }, [isAdmin, initialSelected, groups])

  // Arriving from a "the rules refused this" link: show the pair rather than leaving it
  // somewhere down a table of four thousand rows. Once only — the table re-renders on
  // every map movement, and a page that kept yanking itself back would be unusable.
  const scrollTargetId = isAdmin ? initialSelected[0] : undefined
  useEffect(() => {
    if (scrolledRef.current || !arrivedRef.current) return
    scrolledRef.current = true
    arrivedRef.current.scrollIntoView({ block: 'center', behavior: 'smooth' })
  })

  const selectedGroups = admin.selected
    .map((id) => groups.find((group) => group.id === id))
    .filter(Boolean)

  const refresh = () => dispatch(loadCourseGroups()).catch(() => {})

  const onAdminToggleGroup = (groupId) => {
    if (!isAdmin) return

    const { selected } = admin
    const index = selected.indexOf(groupId)
    // A new array, not a spliced one: the old code mutated this and re-spread the same
    // reference, so nothing downstream could ever see the selection as having changed.
    const next =
      index === -1
        ? [...selected, groupId]
        : selected.filter((id) => id !== groupId)

    // The rename form follows the selection. It used to keep whatever the first course you
    // ever ticked was called, which is how you rename the wrong course.
    const only = next.length === 1 && groups.find((group) => group.id === next[0])
    setCourseState(null)
    setAdmin({
      selected: next,
      name: only ? only.name : '',
      secondaryName: only ? only.secondaryName : '',
    })
  }

  const onAdminUpdateName = (event) => {
    console.log('onAdminUpdateName', event.target.name, event.target.value)
    setAdmin({ ...admin, name: event.target.value })
  }

  const onAdminUpdateSecondaryName = (event) => {
    console.log(
      'onAdminUpdateSecondaryName',
      event.target.name,
      event.target.value
    )
    setAdmin({ ...admin, secondaryName: event.target.value })
  }

  const onAdminRenameSelected = (event) => {
    event.preventDefault()
    event.stopPropagation()

    const { selected, name, secondaryName } = admin
    dispatch(adminRenameGroup(selected[0], name, secondaryName))
      .then(refresh)
      .catch(() => {})
    setAdmin({ selected: [], name: '', secondaryName: '' })
  }

  // Hiding and flagging are one course at a time. Neither bumps `updated` -- no admin
  // action does -- but both reach every installed app through the catalog sync, which
  // windows on `catalog_updated`: this is not a display filter, it is the course going away.
  //
  // No confirm dialog: every one of these has an inverse a click away, and the panel it is
  // clicked from says which course is selected. On success the row is dropped from the
  // selection and greyed instead, which is the acknowledgement. The course list is not
  // refetched; the page patches its own copy of the flags (`onCourseStateChanged`) and keeps
  // the row on screen, greyed and still selectable, whatever the hidden-courses toggle says,
  // until the page is reloaded.
  const onSetCourseState = (group, changes) => {
    if (!isAdmin || busy) return

    setBusy(true)
    dispatch(adminSetCourseState(group.id, changes))
      .then(() => {
        onCourseStateChanged(group.id, changes)
        setCourseState(null)
        setAdmin({ selected: [], name: '', secondaryName: '' })
      })
      .catch(() => {})
      .then(() => setBusy(false))
  }

  const renderSingleAdminOptions = (group) => {
    const { name, secondaryName } = admin
    // Until the preview answers, we do not know whether this course is already hidden --
    // and offering "Hide" for something already hidden is worse than offering nothing.
    const hidden = courseState ? courseState.active === false : null

    return (
      <div className="sticky-panel-bottom">
        <Form onSubmit={onAdminRenameSelected}>
          <Form.Group className="mb-3" controlId="name">
            <Form.Label>Name</Form.Label>
            <Form.Control
              type="text"
              placeholder="Group name"
              value={name}
              onChange={onAdminUpdateName}
            />
          </Form.Group>
          <Form.Group className="mb-3" controlId="secondaryName">
            <Form.Label>Secondary name</Form.Label>
            <Form.Control
              type="text"
              placeholder="Geographical area"
              value={secondaryName}
              onChange={onAdminUpdateSecondaryName}
            />
          </Form.Group>
          <div className="d-flex gap-2 align-items-center flex-wrap">
            <Button variant="danger" type="submit" disabled={busy}>
              Rename group {group.id}
            </Button>
            <Button
              variant={hidden ? 'outline-success' : 'outline-danger'}
              size="sm"
              disabled={busy || hidden === null}
              onClick={() =>
                onSetCourseState(group, { active: hidden })
              }
            >
              {hidden ? 'Un-hide' : 'Hide'}
            </Button>
            {courseState && (
              <span className="text-muted small">
                {hidden ? 'currently hidden' : 'visible'}
              </span>
            )}
          </div>
        </Form>

        {/* Loaded for one course as well as for many: it is the only thing that says
            whether this course is hidden, and it doubles as a look at what is on it. */}
        <MergePanel
          groups={[group]}
          onState={setCourseState}
          onDone={() => {
            setAdmin({ selected: [], name: '', secondaryName: '' })
            refresh()
          }}
        />
      </div>
    )
  }

  const renderAdminOptions = () => {
    if (!isAdmin) return null

    const { selected } = admin
    if (selected.length === 1 && selectedGroups.length === 1) {
      return renderSingleAdminOptions(selectedGroups[0])
    }
    if (selected.length > 1) {
      return (
        <div className="sticky-panel-bottom">
          {/* Not `admin/groups/merge` any more. That one decides the survivor's
              description, directions, coordinates and metadata silently, and the better
              data is very often on the course being absorbed. */}
          <MergePanel
            groups={selectedGroups}
            onDone={() => {
              setAdmin({ selected: [], name: '', secondaryName: '' })
              refresh()
            }}
          />
        </div>
      )
    }
    return null
  }

  const toggledOrder = (columnName, order) => {
    const { sortOptions } = SORT_CONFIG.columns[columnName]
    const index = sortOptions.indexOf(order)
    return sortOptions[(index + 1) % sortOptions.length]
  }

  const onSortClicked = (columnName) => {
    let _sort = sort

    const item = _sort.find(({ key }) => key === columnName)
    if (!!item) {
      _sort = _sort.filter((obj) => {
        return obj.key !== columnName
      })
    }

    const order = toggledOrder(columnName, (item || {}).order)
    if (order !== undefined) {
      _sort.push({
        key: columnName,
        order,
      })
    }

    setSort(_sort)
  }

  const renderSort = (columnName) => {
    const columnSortConfig = sort.find(({ key }) => key === columnName)
    const { order } = columnSortConfig || {}
    const upClass = 'bi bi-caret-up' + (order === 'asc' ? '-fill' : '')
    const downClass = 'bi bi-caret-down' + (order === 'desc' ? '-fill' : '')
    const columnSortPrio = sort.indexOf(columnSortConfig)

    return (
      <div className="sort-column--order">
        <div className="sort-column--order--carets">
          <i className={upClass}></i>
          <i className={downClass}></i>
        </div>
        <div className="sort-column--order--priority">
          {columnSortPrio !== -1 ? columnSortPrio + 1 : ''}
        </div>
      </div>
    )
  }

  const renderDirectionsLink = ({ lat, lng }) => {
    // TODO Add origin if available: &origin=34.1030032,-118.41046840000001
    const destination = `${lat},${lng}`
    return (
      <a // eslint-disable-line jsx-a11y/anchor-has-content
        href={`https://www.google.com/maps/dir/?api=1&destination=${destination}`}
        rel="noreferrer"
        target="_blank"
        className="bi-box-arrow-up-right"
      ></a>
    )
  }

  const renderTableHeader = (label, column) => (
    <th
      className={`sort-column column-${column}`}
      onClick={() => onSortClicked(column)}
    >
      {label}
      {renderSort(column)}
    </th>
  )

  const renderTableItem = (group, id, formatter = (s) => s) => (
    <td className={`column-${id}`} onClick={() => onCourseGroupClicked(group)}>
      {formatter(group[id], group)}
    </td>
  )

  if (!groups || groups.length === 0) return null

  const reverseSort = R.reverse(sort)
  // A copy, and with `played` folded in: the sorter reads `a[key]`, and sorting the prop
  // array in place mutates the store's own list.
  const sortedGroups = groups
    .map((group) =>
      played ? { ...group, played: played[group.id] ?? 0 } : group
    )
    .sort((a, b) => {
      for (let s of reverseSort) {
        const result = SORT_CONFIG.columns[s.key].sortFn(a[s.key], b[s.key])
        if (result !== 0) {
          if (s.order === 'desc') return result
          else return -result
        }
      }
      return 0
    })

  const showCountry = R.uniqBy((group) => group.country, groups).length > 1
  const showState = R.uniqBy((group) => group.state, groups).length > 1

  return (
    <div>
      {renderAdminOptions()}
      <Table hover className="courses-table">
        <thead>
          <tr>
            {isAdmin && renderTableHeader('ID', 'id')}
            {renderTableHeader('Course', 'name')}
            {showCountry && renderTableHeader('Country', 'country')}
            {showState && renderTableHeader('State', 'state')}
            {renderTableHeader('Holes', 'holes')}
            {renderTableHeader('Layouts', 'count')}
            {renderTableHeader('Directions', 'location')}
            {renderTableHeader('Updated', 'updated')}
            {isAdmin && played && (
              <th
                className="sort-column column-played"
                title="Rounds ever recorded on this course's layouts. Never goes down when a game is deleted, and it is what a merge moves."
                onClick={() => onSortClicked('played')}
              >
                Played
                {renderSort('played')}
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {sortedGroups.map((group) => {
            const updated = format(group.updated, 'd MMM yy')
            const arrivedWith = isAdmin && initialSelected.indexOf(group.id) !== -1
            // Greyed, with the reasons spelled out, for every course a visitor is not shown --
            // including one hidden from this page a moment ago.
            const reasons = isAdmin ? hiddenReasons(visibility, group.id) : []
            const muted = reasons.length > 0
            return (
              <tr
                key={group.id}
                ref={group.id === scrollTargetId ? arrivedRef : undefined}
                className={
                  'courses-table-row' +
                  (arrivedWith ? ' courses-table-row--arrived' : '') +
                  (muted ? ' courses-table-row--muted' : '')
                }
              >
                {isAdmin && (
                  <td className="column-id">
                    <Form.Check
                      type="checkbox"
                      label={group.id}
                      checked={admin.selected.indexOf(group.id) !== -1}
                      onChange={() => onAdminToggleGroup(group.id)}
                      key={`key${group.id}`}
                      id={group.id}
                    />
                  </td>
                )}
                {renderTableItem(
                  group,
                  'name',
                  (name, group) =>
                    <>
                      {`${group.name}${
                        group.googleRegion ? ', ' + group.googleRegion : ''
                      }`}
                      {reasons.map((reason) => (
                        <span
                          key={reason}
                          className="badge bg-secondary ms-1 fw-normal"
                        >
                          {reason}
                        </span>
                      ))}
                    </>
                )}
                {showCountry &&
                  renderTableItem(group, 'country', (country) =>
                    nameCountryById(country)
                  )}
                {showState &&
                  renderTableItem(group, 'state', (state) =>
                    nameStateById(state)
                  )}
                {renderTableItem(group, 'holes')}
                {renderTableItem(group, 'count')}
                {group.location && (
                  <td className="column-location">
                    {renderDirectionsLink(group.location)}
                  </td>
                )}
                {!group.location && renderTableItem(group, 'location')}
                {renderTableItem(group, 'updated', () => updated)}
                {isAdmin && played && (
                  <td className="column-played">{played[group.id] ?? 0}</td>
                )}
              </tr>
            )
          })}
        </tbody>
      </Table>
    </div>
  )
}

const mapStateToProps = (state) => {
  return {
    isAdmin: checkIsAdmin(state.auth.user),
  }
}

export default connect(mapStateToProps)(GroupsTable)
